import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Trash2, Plus, ArrowLeft, RefreshCw, Lock, EyeOff, Eye } from "lucide-react";

/**
 * Iter-148 · Phase-1 · Dealership 360 configurable master lists.
 *
 * Renders CRUD panels for 8 taxonomies driven by the backend
 * `/dealership/masters/{list_type}` API. Hierarchical lists
 * (Manufacturer → Model → Variant) get a parent picker.
 *
 * data-testids: cfg-list-tab-{key}, cfg-add-btn, cfg-name-input,
 *   cfg-row-{name}, cfg-delete-{name}, cfg-parent-select.
 */
const HIERARCHY_ORDER = ["manufacturer", "model", "variant"];

export default function MastersConfigurableLists() {
  const [types, setTypes] = useState([]);
  const [active, setActive] = useState("manufacturer");

  useEffect(() => {
    api.get("/dealership/masters/list-types")
      .then((r) => setTypes(r.data?.list_types || []))
      .catch(() => setTypes([]));
  }, []);

  return (
    <div className="space-y-3" data-testid="masters-config-lists">
      <div className="flex flex-wrap gap-1.5 border-b pb-2">
        {types.map((t) => (
          <button
            key={t.key}
            onClick={() => setActive(t.key)}
            data-testid={`cfg-list-tab-${t.key}`}
            className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider rounded-sm border transition-colors ${
              active === t.key
                ? "border-accent bg-accent/10 text-accent"
                : "border-border hover:border-accent/40"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {active && (
        <ListPanel
          listType={active}
          meta={types.find((t) => t.key === active) || {}}
          onSwitchToParent={(key) => setActive(key)}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────

function ListPanel({ listType, meta, onSwitchToParent }) {
  const [rows, setRows] = useState([]);
  const [parents, setParents] = useState([]);
  const [parentFilter, setParentFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [editOpen, setEditOpen] = useState(null); // row being edited
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", parent_id: "" });

  const parentType = meta.parent;

  const load = async () => {
    setBusy(true);
    try {
      const params = {};
      if (parentType && parentFilter) params.parent_id = parentFilter;
      if (!showInactive) params.active_only = true;
      const r = await api.get(`/dealership/masters/${listType}`, { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setBusy(false);
    }
  };

  const loadParents = async () => {
    if (!parentType) return setParents([]);
    try {
      const r = await api.get(`/dealership/masters/${parentType}`, { params: { active_only: true } });
      setParents(r.data || []);
    } catch { setParents([]); }
  };

  useEffect(() => { loadParents(); setParentFilter(""); /* reset when switching list */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listType]);
  useEffect(() => { load(); /* refetch on parent filter change or inactive toggle */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listType, parentFilter, showInactive]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (parentType && !form.parent_id) return toast.error(`Pick a ${parentType} first`);
    try {
      await api.post(`/dealership/masters/${listType}`, form);
      toast.success(`${meta.label || listType} added`);
      setAddOpen(false); setForm({ name: "", parent_id: parentFilter || "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };

  const update = async () => {
    if (!editOpen) return;
    try {
      await api.patch(`/dealership/masters/${listType}/${editOpen.id}`, {
        name: editOpen.name, active: editOpen.active,
      });
      toast.success("Updated"); setEditOpen(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const remove = async (row) => {
    if (row.system) {
      const ok = window.confirm(`"${row.name}" is a system row and will be deactivated (not deleted). Continue?`);
      if (!ok) return;
    } else if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      const r = await api.delete(`/dealership/masters/${listType}/${row.id}`);
      if (r.data?.deactivated) toast.info(r.data.reason || "Deactivated instead of deleted");
      else toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">{meta.label || listType}</div>
          <div className="text-[11px] text-muted-foreground">
            {parentType
              ? <>Hierarchical list — each row belongs to a <b>{parentType}</b>.</>
              : <>Flat list — used across Leads, Quotations and Bookings.</>}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setShowInactive((s) => !s)}
          className="rounded-sm h-8 text-[11px] gap-1" data-testid="cfg-toggle-inactive">
          {showInactive ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {showInactive ? "Hide inactive" : "Show inactive"}
        </Button>
        <Button variant="ghost" size="sm" onClick={load} className="rounded-sm h-8 gap-1" title="Refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
        </Button>
        <Button onClick={() => { setForm({ name: "", parent_id: parentFilter || "" }); setAddOpen(true); }}
          className="rounded-sm h-8 bg-accent hover:bg-accent/90 text-white gap-1" data-testid="cfg-add-btn">
          <Plus className="w-3.5 h-3.5" /> Add {meta.label?.split(" ").pop() || listType}
        </Button>
      </div>

      {/* Parent filter — only for hierarchical children */}
      {parentType && (
        <div className="flex items-center gap-2 border border-dashed rounded-sm p-2 bg-muted/20">
          <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground shrink-0">
            Filter by {parentType}:
          </Label>
          <Select value={parentFilter} onValueChange={setParentFilter}>
            <SelectTrigger className="rounded-sm h-8 max-w-xs" data-testid="cfg-parent-filter">
              <SelectValue placeholder={`All ${parentType}s`} />
            </SelectTrigger>
            <SelectContent>
              {parents.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {parentFilter && (
            <Button variant="ghost" size="sm" onClick={() => setParentFilter("")} className="h-8 text-[10px]">
              Clear
            </Button>
          )}
          <Button variant="link" size="sm" onClick={() => onSwitchToParent(parentType)}
            className="ml-auto h-8 text-[10px] text-accent gap-1">
            <ArrowLeft className="w-3 h-3" /> Manage {parentType}s
          </Button>
        </div>
      )}

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead>
            <tr>
              <th className="text-left">Name</th>
              {parentType && <th className="text-left">{parentType}</th>}
              <th className="text-left">Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={parentType ? 4 : 3} className="text-center py-8 text-muted-foreground text-xs">
                  {parentType && !parentFilter
                    ? `Pick a ${parentType} above to see its ${meta.label?.toLowerCase() || listType}s, or add a new row.`
                    : "No rows yet — click Add to create the first one."}
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const parent = parents.find((p) => p.id === r.parent_id);
              return (
                <tr key={r.id} data-testid={`cfg-row-${r.name.replace(/\s+/g, '-').toLowerCase()}`}
                  className={r.active ? "" : "opacity-50"}>
                  <td>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium">{r.name}</span>
                      {r.system && <Lock className="w-3 h-3 text-amber-500" title="System row — deactivatable but not deletable" />}
                    </div>
                  </td>
                  {parentType && (
                    <td className="text-xs text-muted-foreground">{parent?.name || <span className="italic">— (deleted)</span>}</td>
                  )}
                  <td>
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm ${
                      r.active ? "bg-emerald-500/10 text-emerald-600" : "bg-muted text-muted-foreground"
                    }`}>{r.active ? "Active" : "Inactive"}</span>
                  </td>
                  <td className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditOpen({ ...r })} className="h-7 text-[10px]">Edit</Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(r)}
                      className="h-7 text-[10px] text-red-500 hover:text-red-600" data-testid={`cfg-delete-${r.name.replace(/\s+/g, '-').toLowerCase()}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add sheet */}
      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader>
            <SheetTitle className="font-heading">Add {meta.label}</SheetTitle>
            <SheetDescription className="sr-only">Create a new row in the {meta.label} master list.</SheetDescription>
          </SheetHeader>
          {parentType && (
            <div>
              <Label>{parentType} *</Label>
              <Select value={form.parent_id} onValueChange={(v) => setForm({ ...form, parent_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="cfg-parent-select"><SelectValue placeholder={`Pick a ${parentType}`} /></SelectTrigger>
                <SelectContent>
                  {parents.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-sm mt-1" autoFocus data-testid="cfg-name-input" />
          </div>
          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="cfg-save">
            Save
          </Button>
        </SheetContent>
      </Sheet>

      {/* Edit sheet */}
      <Sheet open={!!editOpen} onOpenChange={(v) => !v && setEditOpen(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader>
            <SheetTitle className="font-heading">Edit {meta.label}</SheetTitle>
            <SheetDescription className="sr-only">Rename or (de)activate this master row.</SheetDescription>
          </SheetHeader>
          {editOpen && (
            <>
              <div>
                <Label>Name</Label>
                <Input value={editOpen.name} onChange={(e) => setEditOpen({ ...editOpen, name: e.target.value })}
                  className="rounded-sm mt-1" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!editOpen.active}
                  onChange={(e) => setEditOpen({ ...editOpen, active: e.target.checked })} />
                Active
              </label>
              <Button onClick={update} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white">
                Save
              </Button>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
